// Birthday utilities for KinsCircle family graph.
// Supports full DOB, month+day only, year only, and unknown.
// Honors per-member visibility ('public' | 'private' | 'year_only').

export type BirthdayVisibility = 'public' | 'private' | 'year_only';

export interface BirthdayFields {
  birthDate?: string | null; // YYYY-MM-DD when fully known
  birthYear?: number | null;
  birthMonth?: number | null; // 1-12
  birthDay?: number | null;   // 1-31
  birthdayVisibility?: BirthdayVisibility | null;
}

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const MONTHS_SHORT = [
  'Jan','Feb','Mar','Apr','May','Jun',
  'Jul','Aug','Sep','Oct','Nov','Dec',
];

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Pull out month/day/year from whichever fields are populated.
// Returns nulls when unknown.
export const extractBirthday = (m: BirthdayFields) => {
  let month = m.birthMonth ?? null;
  let day = m.birthDay ?? null;
  let year = m.birthYear ?? null;

  if (m.birthDate) {
    // Parse YYYY-MM-DD without timezone surprises.
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(m.birthDate);
    if (match) {
      year = year ?? parseInt(match[1], 10);
      month = month ?? parseInt(match[2], 10);
      day = day ?? parseInt(match[3], 10);
    }
  }
  return { month, day, year };
};

// What level of detail can the viewer see for this birthday?
// Note: we treat 'private' as fully hidden — caller should branch on this.
export const visibleParts = (m: BirthdayFields) => {
  const vis = (m.birthdayVisibility || 'public') as BirthdayVisibility;
  const { month, day, year } = extractBirthday(m);
  if (vis === 'private') return { month: null, day: null, year: null, visibility: vis };
  if (vis === 'year_only') return { month: null, day: null, year, visibility: vis };
  return { month, day, year, visibility: vis };
};

// Display string honoring visibility & partial data.
// Examples:
//   "March 14, 1965"   → full
//   "March 14"         → month + day only
//   "1965"             → year only
//   "Private"          → visibility=private
//   ""                 → unknown
export const formatBirthday = (m: BirthdayFields): string => {
  const vis = (m.birthdayVisibility || 'public') as BirthdayVisibility;
  if (vis === 'private') return 'Private';
  const parts = visibleParts(m);
  if (parts.month && parts.day && parts.year) {
    return `${MONTHS[parts.month - 1]} ${parts.day}, ${parts.year}`;
  }
  if (parts.month && parts.day) {
    return `${MONTHS[parts.month - 1]} ${parts.day}`;
  }
  if (parts.year) return `${parts.year}`;
  return '';
};

export const formatBirthdayShort = (m: BirthdayFields): string => {
  const vis = (m.birthdayVisibility || 'public') as BirthdayVisibility;
  if (vis === 'private') return '';
  const parts = visibleParts(m);
  if (parts.month && parts.day && parts.year) {
    return `${MONTHS_SHORT[parts.month - 1]} ${parts.day}, ${parts.year}`;
  }
  if (parts.month && parts.day) {
    return `${MONTHS_SHORT[parts.month - 1]} ${parts.day}`;
  }
  if (parts.year) return `b. ${parts.year}`;
  return '';
};

// Compute the next upcoming birthday (today or in the future).
// Returns null if month+day are unknown OR visibility hides month/day.
// Uses a "rolling calendar" — does NOT require the year to be known.
export const nextBirthday = (m: BirthdayFields, from: Date = new Date()) => {
  const parts = visibleParts(m);
  if (!parts.month || !parts.day) return null;

  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let candidate = new Date(today.getFullYear(), parts.month - 1, parts.day);
  // Handle Feb 29 in non-leap years → fall back to Feb 28
  if (candidate.getMonth() !== parts.month - 1) {
    candidate = new Date(today.getFullYear(), parts.month - 1, 28);
  }
  if (candidate.getTime() < today.getTime()) {
    candidate = new Date(today.getFullYear() + 1, parts.month - 1, parts.day);
    if (candidate.getMonth() !== parts.month - 1) {
      candidate = new Date(today.getFullYear() + 1, parts.month - 1, 28);
    }
  }
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntil = Math.round((candidate.getTime() - today.getTime()) / msPerDay);

  // Calculate "turning age" if year is known
  let turningAge: number | null = null;
  if (parts.year) {
    turningAge = candidate.getFullYear() - parts.year;
  }

  return {
    date: candidate,
    daysUntil,
    turningAge,
    label: `${MONTHS[parts.month - 1]} ${ordinal(parts.day)}`,
  };
};

// Friendly countdown phrase. Returns null if no countdown applies.
export const countdownLabel = (m: BirthdayFields): string | null => {
  const next = nextBirthday(m);
  if (!next) return null;
  if (next.daysUntil === 0) return 'Birthday today!';
  if (next.daysUntil === 1) return 'Birthday tomorrow';
  if (next.daysUntil <= 14) return `In ${next.daysUntil} days`;
  if (next.daysUntil <= 31) return `In ${Math.round(next.daysUntil / 7)} weeks`;
  return null;
};

// Should we surface a countdown badge on the profile card?
export const isBirthdaySoon = (m: BirthdayFields, daysWindow = 30) => {
  const next = nextBirthday(m);
  return !!next && next.daysUntil <= daysWindow;
};

export const monthOptions = MONTHS.map((label, i) => ({ value: i + 1, label }));

// Days in a given month (assumes non-leap; safe for input validation)
export const daysInMonth = (month: number, year?: number | null) => {
  if (!month) return 31;
  if (year) return new Date(year, month, 0).getDate();
  // Non-leap year approximation
  return new Date(2001, month, 0).getDate();
};
